/**
 * Panel Event Protocol — the suite-standard window event names (D4).
 *
 * The reference implementation used bare custom-event names; the package
 * namespaces them as `panel:*` so shell events can never collide with an
 * application's own window events. Every dispatch and listener in the shell
 * goes through these constants.
 */

export const PANEL_EVENTS = {
    /** Show/hide a panel by canonical id (menus, commands) */
    togglePanel: 'panel:toggle-panel',
    /** Activate (select) a tab inside its group */
    activateTab: 'panel:activate-tab',
    /** Ask the primary shell to rebuild a panel tab from defaults */
    requestRestoreTab: 'panel:request-restore-tab',
    /** Broadcast: canonical panel id became visible/hidden */
    visibilityChange: 'panel:visibility-change',
    /** Ask every shell to re-report its visible panels */
    requestVisibility: 'panel:request-visibility',
    /** Apply a named workspace layout */
    applyWorkspace: 'panel:apply-workspace',
    /** Back to the factory layout */
    resetWorkspace: 'panel:reset-workspace',
    /** Ask the primary shell for its current layout (New Workspace) */
    captureWorkspace: 'panel:capture-workspace',
    /** The shell's answer to captureWorkspace */
    captureWorkspaceResult: 'panel:capture-workspace-result',
    /** Move a tab between two docked groups */
    transferTab: 'panel:transfer-tab',
    /** A floating window's tab was dropped onto a docked group */
    floatingWindowDrop: 'panel:floating-window-drop',
    /** Remove a tab from its group entirely */
    removeTabFromPanel: 'panel:remove-tab',
    /** Internal: a toolbar drag-reorder just began (closes submenus) */
    toolbarDragActive: 'panel:toolbar-drag-active',
    /** A panel instance removed itself from the document */
    panelClosed: 'panel:closed',
} as const;

export type PanelEventName = (typeof PANEL_EVENTS)[keyof typeof PANEL_EVENTS];
